/* eslint-disable no-param-reassign */
import React, {
    useContext,
    useCallback,
    useState,
    useEffect,
    useMemo
} from 'react';
import _ from 'lodash';
import appConfig from 'app-config';
import {
    FormattedMessage,
    useTranslator
} from '@jutro/locale';
import {
    WizardPage,
    wizardProps
} from '@xengage/gw-portals-wizard-react';
import {
    ViewModelServiceContext,
    ViewModelForm,
    useDataRefresh
} from '@xengage/gw-portals-viewmodel-react';
import { ViewModelUtil } from '@xengage/gw-portals-viewmodel-js';
import { MLPAEditVehicleComponent, MLModifierComponent } from 'ml-components-common-react';
import { MLMultiLobsDataHolderService } from 'ml-services-common-react';
import {
    useValidation
} from '@xengage/gw-portals-validation-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { LoadSaveService, MLAddressVerificationService } from 'gw-capability-quoteandbind';
import {
    MockUpUtil
} from '@xengage/gw-portals-util-js';
import {
    readViewModelValue
} from '@xengage/gw-jutro-adapters-react';
import { MLModalService } from 'ml-portals-services-react';
import { platformMessages } from 'ml-components-platform-react';
import { MLHVQDefaultFieldsUtil, MLModifierUtil } from 'ml-portals-util-js';
import { MLNotificationBlockUtil, MLWizardNavUtil } from 'ml-portals-util-react';
import metadata from './VehiclesPage.metadata.json5';
import PATrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PATrackEvent';
import messages from './VehiclesPage.messages';

import styles from './VehiclesPage.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';
import moment from 'moment';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function VehiclesPage(props) {
    const {
        wizardData: submissionVM,
        updateWizardData,
        goNext,
        isSkipping,
        currentStep
    } = props;
    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const translator = useTranslator();
    const [isPageInitialised, updateIsPageInitialised] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);

    const {
        refreshData
    } = useDataRefresh();
    const paModel = submissionVM.lobData.personalAuto.coverables.value;

    const [showValidtionErrors, setShowValidtionErrors] = useState(false);

    const { onValidate } = useValidation('VehiclesPage');
    const { authHeader } = useAuthentication();
    const PoBoxRegExpression = '^(.*?)([Pp]\\.? ?[Oo]\\.? ?[Bb][Oo][Xx])(.*)$';
    const POBoxRegex = new RegExp(PoBoxRegExpression);

    const productCode = _.get(submissionVM, 'baseData.productCode.value');
    const isQnB = appConfig.portalCode === 'QB';
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const [showGarageAddressError, setShowGarageAddressError] = useState(false);

    const { nextStepVisited } = MLWizardNavUtil(props);

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    const {
        addWarningAlert,
        clearBlockAlerts,
        addErrorAlert,
        clearErrorAlerts
    } = MLNotificationBlockUtil();

    const sortedVehicleVMList = useMemo(() => {
        const unsoredtedVehicleVmList = paModel.vehicles.map((vehicle) => {
            return submissionVM.lobData.personalAuto.coverables.vehicles.findFirstElement(vehicle);
        });
        return _.sortBy(unsoredtedVehicleVmList, 'publicID.value');
    }, [paModel.vehicles, submissionVM.lobData.personalAuto.coverables.vehicles]);

    const setEditingVehicleIndex = (p) => {
        const indexOfNewlyAddedVehicle = sortedVehicleVMList.findIndex((vehicleVM) => vehicleVM.value.tempID);
        if (indexOfNewlyAddedVehicle >= 0) {
            p.currentIdx = indexOfNewlyAddedVehicle;
        }
        p.currentVehicleVM = sortedVehicleVMList[p.currentIdx];
        p.currentVehicleVM.editMode_ML.value = true;
    };

    const initPageState = () => {
        const p = {
            currentIdx: 0,
            currentVehicleVM: {},
            policyAddress: {},
            effectiveDate: ''
        };
        setEditingVehicleIndex(p);
        return p;
    };

    const nextPageState = (vehicleIndex) => {
        const p = {
            currentIdx: vehicleIndex,
            policyAddress: submissionVM.baseData.policyAddress,
            effectiveDate: submissionVM.baseData.periodStartDate.value
        };
        p.currentVehicleVM = sortedVehicleVMList[p.currentIdx];
        return p;
    };

    const [page, setPage] = useState(initPageState);

    const {
        HVQConsumerSteps
    } = appConfig;

    const isCompRaterDefaultsPage = new Set(HVQConsumerSteps).has(currentStep.mlStepName);
    const nextMessagePendingVehicles = isCompRaterDefaultsPage ? messages.wellReviewYourAdditionalVehiclesNext : messages.wellWorkYourAdditionalVehiclesNext;
    const nextMessageFinalVehicle = isCompRaterDefaultsPage ? messages.wellReviewYourAdditionalInfoNext : messages.wellGatherYourAdditionalInfoNext;

    const isConnectedCar = _.get(page.currentVehicleVM, 'vehicleModifiers_ML.value[0].codeIdentifier_ML' ) === "PAConnectedCar_ML";
    const shouldVisible = ()=>{
        if(isQnB){
            return !isConnectedCar;
        }

        return true;
    }
  
    const createVehicleVM = useCallback(() => {
        const vehicles = submissionVM.lobData.personalAuto.coverables.vehicles.value;
        if (_.isEmpty(vehicles)) {
            const primaryNamedInsured = submissionVM.baseData.primaryNamedInsured_ML.value;
            const vehicleObj = {
                licenseState: primaryNamedInsured.primaryAddress.state,
                costNew: {
                    amount: ''
                },
                primaryUse: 'commuting'

            };

            const {
                _xCenter,
                _dtoName
            } = submissionVM.lobData.personalAuto.coverables.vehicles;
            const vehicleVM = viewModelService.create(vehicleObj, _xCenter, _dtoName);
            submissionVM.lobData.personalAuto.coverables.vehicles.pushElement(vehicleVM);
            updateWizardData(submissionVM);
        }
    }, [submissionVM, updateWizardData, viewModelService]);

    const skipOnResumeIf = useCallback(() => {
        if (!!_.get(submissionVM, 'bindData.policyNumber.value')
            && (submissionVM.value.baseData.status_ML === 'Bound' || submissionVM.value.baseData.status_ML === 'Binding')) {
            return true;
        }

        if (submissionVM.value.isConsumerCompRater()) {
            return MLMultiLobsDataHolderService.hasCompletedStep(currentStep.mlStepName);
        }

        if (currentStep.mlStepName === 'paHVQConsumerVehicleDetails' && !(submissionVM.hvqQuoteID_ML && submissionVM.hvqQuoteID_ML.value)) {
            return true;
        }

        if (MLMultiLobsDataHolderService.getLastVisitedStep() === 'paVehicleDetails') {
            return false;
        }

        if (MLMultiLobsDataHolderService.getLastVisitedStep() === 'quoteDetails' &&
            submissionVM.hvqQuoteID_ML.value &&
            submissionVM.isBundle_ML.value) {
            return true;
        }

        if (submissionVM.isBundle_ML.value
            && submissionVM.value.baseData.status_ML !== 'Draft') {
            return true;
        }

        return submissionVM.value.lobData.personalAuto.coverables.vehicles[0]
            && submissionVM.value.lobData.personalAuto.coverables.vehicles[0].costNew;
    }, [currentStep.mlStepName, submissionVM]);


    const indexOfVehicle = useCallback((vehicle) => {
        return sortedVehicleVMList.indexOf(vehicle);
    }, [sortedVehicleVMList]);

    const isFinalVehicle = useCallback((vehicle) => {
        return indexOfVehicle(vehicle) === (sortedVehicleVMList.length - 1);
    }, [indexOfVehicle, sortedVehicleVMList.length]);

    const showCommuteWarning = useCallback((vehicle) => {
        if (isFinalVehicle(vehicle) && vehicle.primaryUse.value !== undefined) {
            const employedDrivers = paModel.drivers.find((d) => {
                return d.occupation_Ext && (d.occupation_Ext !== 'unemployed' && d.occupation_Ext !== 'retired' && d.occupation_Ext !== 'student');
            });
            const commuteVehicles = paModel.vehicles.find((v) => {
                return v.primaryUse && (v.primaryUse === 'school_or_work_ml' || v.primaryUse === 'business');
            });
            return !!employedDrivers && !commuteVehicles;
        }
        return false;
    }, [isFinalVehicle, paModel.drivers, paModel.vehicles]);

    const setCommuteAlert = useCallback((vehicle) => {
        const warningMsg = messages.commuteAlert;
        if (showCommuteWarning(vehicle)) {
            addWarningAlert(warningMsg, 'vehiclesNotListedAsCommute');
        } else {
            clearBlockAlerts('vehiclesNotListedAsCommute');
        }
    }, [addWarningAlert, clearBlockAlerts, showCommuteWarning]);

    const isModifierSelected = (modifiers, modifierCode) => {
        const modifier = modifiers.find((mod) => mod.codeIdentifier_ML === modifierCode);
        if (modifier) {
            return modifier.chosenTermValue === "true";
        }
        return false;
    };

    const getCategoryByCode = (code, vehicle) => {
        return vehicle.category_ML.aspects.availableValues.find((category) => {
            return category.code === code;
        });
    };

    const isCategory = (code, vehicle) => {
        return vehicle.category_ML.value === getCategoryByCode(code, vehicle);
    };

    const isPrivatePassengerNP = (vehicle) => {
        return isCategory('private_passenger_np', vehicle);
    }

    useEffect(() => {
            if (!isSkipping) {
                PATrackEvent('Quote Step Reached', false, submissionVM, null, { step: 'Vehicles Screen'}, true);
            }
    }, [isSkipping])
    
    useEffect(() => {
        if (!isPageInitialised && (!isSkipping || (isSkipping && !skipOnResumeIf()))) {
            const vehiclesPath = 'lobData.personalAuto.coverables.vehicles';
            const vehicles = _.get(submissionVM, `${vehiclesPath}.value`);
            page.policyAddress = submissionVM.baseData.policyAddress;
            page.effectiveDate = submissionVM.baseData.periodStartDate.value;
            const initVehicleValue = (v) => {
                if (!v.value_ML || v.value_ML.amount === 0) {
                    v.value_ML = {
                        amount: undefined,
                        currency: 'usd'
                    };
                }
            };

            vehicles.forEach((veh) => {
                veh.editMode_ML = true;
                initVehicleValue(veh);
            });


            if (!!vehicles && vehicles.length > 0) {
                vehicles.forEach((vehicle) => {
                    vehicle.financeCategory_ML = vehicle.financeCategory_ML || 'owned';
                    vehicle.namedNonOwner_ML = false;
                });
            }

            const newVehicles = vehicles.map((vehicle, index) => {
                const pathToCheckForMockData = `${vehiclesPath}.${index}`;
                const isLincenseNumberMocked = MockUpUtil.isValueMocked(
                    submissionVM.value,
                    'quote.pa',
                    `${pathToCheckForMockData}.license`
                );
                const isVINMocked = MockUpUtil.isValueMocked(
                    submissionVM.value,
                    'quote.pa',
                    `${pathToCheckForMockData}.vin`
                );
                const newVehicle = {
                    ...vehicle,
                    vin: isVINMocked ? undefined : vehicle.vin,
                    costNew: isLincenseNumberMocked
                        ? {
                            ...vehicle.costNew,
                            amount: undefined
                        }
                        : vehicle.costNew
                };
                if (isLincenseNumberMocked && newVehicle.license) {
                    delete newVehicle.license;
                }
                return newVehicle;
            });

            if (!_.isEqual(newVehicles, vehicles)) {
                _.set(submissionVM, `${vehiclesPath}.value`, newVehicles);
                updateWizardData(submissionVM);
            }

            if (_.isEmpty(vehicles)) {
                createVehicleVM();
            }
            
            if (!isQnB) {
                setCommuteAlert(page.currentVehicleVM);
            }

            updateIsPageInitialised(true);
        }
    }, [createVehicleVM, isPageInitialised, isSkipping, paModel.vehicles, page, page.effectiveDate, page.policyAddress, setCommuteAlert, skipOnResumeIf, sortedVehicleVMList, submissionVM, updateWizardData]);

    useEffect(() => {
        setCommuteAlert(false);
        setCommuteAlert(page.currentVehicleVM);
    }, [page.currentVehicleVM.value.primaryUse]); // eslint-disable-line react-hooks/exhaustive-deps

    const indexOfDriver = (vehicle) => {
        return sortedVehicleVMList.indexOf(vehicle);
    };


    const generateEditVehicleOverrides = () => {
        const getRightRailRowClassName = (vehicle) => {
            const isCompleteVehicle = (v) => {
                return indexOfDriver(v) < page.currentIdx;
            };

            const isCurrentVehicleVM = (v) => {
                return indexOfDriver(v) === page.currentIdx;
            };

            if (isCompleteVehicle(vehicle)) {
                return 'editable';
            }
            if (isCurrentVehicleVM(vehicle)) {
                return 'current-icon';
            }
            return 'pending-icon';
        };

        const overrides = sortedVehicleVMList.map((vehicle, index) => {
            return {
                [`vehicleIconSpan${index}`]: {
                    className: getRightRailRowClassName(vehicle)
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const vehicleCategoryPrivatePassenger = viewModelService.productMetadata.get('pc').types.getTypelist('VehicleCategory_ML').getCode('private_passenger_light_truck').code;
    const vehicleCategoryCollectible = viewModelService.productMetadata.get('pc').types.getTypelist('VehicleCategory_ML').getCode('collectible').code;
    const vehicleTypePrivatePassengerAuto = viewModelService.productMetadata.get('pc').types.getTypelist('VehicleType').getCode('private_passenger_autos_ml').code;
    const vehicleTypeClassic = viewModelService.productMetadata.get('pc').types.getTypelist('VehicleType').getCode('classic_ml').code;
    const showTrim = (vehicle) => {
        return vehicle.category_ML.value
            && (vehicle.category_ML.value === vehicleCategoryPrivatePassenger || vehicle.category_ML.value === vehicleCategoryCollectible);
    };

    const showCylinders = (vehicle) => {
        return vehicle.type_ML.value && (vehicle.cylinders_ML.value !== undefined)
            && (vehicle.type_ML.value.code === vehicleTypePrivatePassengerAuto || vehicle.type_ML.value.code === vehicleTypeClassic);
    };

    const showBodyTypes = (vehicle) => {
        return vehicle.category_ML.value.code
            && (vehicle.category_ML.value.code === vehicleCategoryPrivatePassenger);
    };

    const getBodyTypeName = (vehicle) => {
        if (vehicle.bodyType_ML.value !== undefined) {
            const bodytypename = translator({
                defaultMessage: vehicle.bodyType_ML.value.name || vehicle.bodyType_ML.value.code,
                id: vehicle.bodyType_ML.value.name
            });

            return bodytypename;
        }
    };
    const generateVehicleListOverrides = () => {
        const overrides = sortedVehicleVMList.map((vehicle, index) => {
            return {
                [`vehicleName${index}`]: {
                    content: (
                        <div>
                            <span>
                                <strong>
                                    {vehicle.year.value}
                                    {' '}
                                    {vehicle.make.value}
                                </strong>
                                {' '}
                            </span>
                            <span>
                                {vehicle.model.value}
                            </span>
                            {showTrim(vehicle) ? (
                                <span>
                                    {' '}
                                    {vehicle.trim_ML.value}
                                </span>
                            ) : undefined}
                            {showBodyTypes(vehicle) ? (
                                <span>
                                    {' '}
                                    {getBodyTypeName(vehicle)}
                                </span>
                            ) : undefined}
                            {showCylinders(vehicle) ? (
                                <span>
                                    {' '}
                                    {vehicle.cylinders_ML.value}
                                    {' cyl'}
                                </span>
                            ) : undefined}
                        </div>
                    )
                }
            };
        });

        return Object.assign({}, ...overrides);
    };


    const handleVehicleModelChange = useCallback(() => {
        refreshData();
    }, [refreshData]);

    const isNonOwnedSelected = (vehicle) => {
        return vehicle.financeCategory_ML === undefined;
    };

    const isVehicleValid = () => {
        page.currentVehicleVM.editMode_ML = true;
        return page.currentVehicleVM.aspects.subtreeValid;
    };

    const isParkedInStateSameAsPrimary = () => {
        return _.isEqual(page.currentVehicleVM.value.garagingLocation_ML.state, page.policyAddress.value.state);
    };

    const isPrivatePassengerCategory = () => {
        if (page.currentVehicleVM.category_ML?.value) {
            switch (page.currentVehicleVM.category_ML.value.code) {
                case 'private_passenger_light_truck':
                    return true;
                default:
                    return false;
            }
        }
        return false;
    };

    const isCollectibleCategory = () => {
        if (page.currentVehicleVM && page.currentVehicleVM.category_ML && vehicleVM.category_ML.value) {
            switch (page.currentVehicleVM.category_ML.value.code) {
                case 'collectible':
                    return true;
                default:
                    return false;
            }
        }
        return false;
    };

        const isStorageUseValid = () => {
            const vehiclesVMPath = 'lobData.personalAuto.coverables.vehicles';
            const allVehicles = _.get(submissionVM, `${vehiclesVMPath}.children`, []);

            let vehicleStorageUse = 0;
            let noOfPrivatePassengerNP = 0;

            allVehicles.forEach((vehicle) => {
                const vehiclePrimaryUse = vehicle?.primaryUse?.value?.code;

                if(isPrivatePassengerNP(vehicle)){
                noOfPrivatePassengerNP++;
            }

                if (vehiclePrimaryUse === 'storage_np') {
                    vehicleStorageUse++;
                }
            });

            // Invalid if all vehicles are Storage
            return vehicleStorageUse > 0 &&  vehicleStorageUse === noOfPrivatePassengerNP;
        };
        const valueAmountIsLessThanThreeThousand = () => {
            const amount = page?.currentVehicleVM?.value_ML?.amount.value;
            // Skip validation if no value is entered
            if (amount === null || amount === undefined || amount === '') {
                return false;
            }
            return Number(amount) < 3000;
        };
        
        const isStorageUseYear = () => {
            const vehicleYear = page?.currentVehicleVM?.year?.value;
            return vehicleYear < 1970 && page.primaryUse?.value?.code === 'storage_np';
        }

    const isPurchaseYearWithinTwoYearsPrior = () => {
        const MAX_ALLOWED_YEARS = 2;
        const vehicleYear = page?.currentVehicleVM?.year?.value;
        const purchaseYear = page?.currentVehicleVM?.vehiclePurchaseDate?.year?.value

        const hasVehicleYear = _.has(page.currentVehicleVM, 'year')
        const hasPurchaseYear = _.has(page.currentVehicleVM, 'vehiclePurchaseDate')

        if (!hasVehicleYear || !hasPurchaseYear) {
            return true;
        } 
        if (!vehicleYear || !purchaseYear) {
            return true;
        }

        const minAllowedPurchaseYear = vehicleYear - MAX_ALLOWED_YEARS;

        return purchaseYear >= minAllowedPurchaseYear;
    };

    const isValid = () => {
        if (isPOBox()) {
            setShowGarageAddressError(true);
            setShowValidtionErrors(true);
            return false;
        }

        setShowGarageAddressError(false);
        if (!isVehicleValid() || !isParkedInStateSameAsPrimary()) {
            setTimeout(() => {
                setShowValidtionErrors(true);
                setErrorTimestamp(errorTimestamp + 1);
            }, 100)
            return false;
        }

        if (valueAmountIsLessThanThreeThousand() && !isModernProduct) {
            clearBlockAlerts('valueAmountIsLessThanThreeThousand');
            addErrorAlert(
                translator(messages.valueAmountIsLessThanThreeThousand),
                'valueAmountIsLessThanThreeThousand'
            );
            return false;
        } else {
            clearBlockAlerts('valueAmountIsLessThanThreeThousand');
        }

        if(page.currentVehicleVM?.purchaseDate_ML?.year === undefined && isQnB && !isModernProduct && isPrivatePassengerCategory() && isCollectibleCategory()){
            setTimeout(() => {
                setShowValidtionErrors(true);
                setErrorTimestamp(errorTimestamp + 1);
            }, 100)

            return false
        } else if (isQnB && isModernProduct) {
            if(isStorageUseYear()) {
                return false;
            }
            if(!checkValidDate(page.currentVehicleVM.value)){
                return false;
            }

            if (isStorageUseValid()) {
                clearBlockAlerts('allVehiclesStorageError');

                addErrorAlert(
                    translator(messages.allVehiclesStorageError),
                    'allVehiclesStorageError'
                );

                return false;
            } else {
                clearBlockAlerts('allVehiclesStorageError');
            }

            if(!isPurchaseYearWithinTwoYearsPrior()){
                const isPurchaseYearWithinTwoYearsPriorError = translator(messages.maxTwoYearsAllowedForPurchaseYear, {
                    vehicleName: page?.currentVehicleVM?.displayName?.value
                });
                clearBlockAlerts("isPurchaseYearWithinTwoYearsPriorError")
                addErrorAlert(isPurchaseYearWithinTwoYearsPriorError, "isPurchaseYearWithinTwoYearsPriorError")
                return false
            } else {
                clearBlockAlerts("isPurchaseYearWithinTwoYearsPriorError")
                return true
            }
        }
        
        return true;
    };

    const isPOBox = () => {
        return POBoxRegex.test(page.currentVehicleVM.value.garagingLocation_ML.addressLine1) ||
            POBoxRegex.test(page.currentVehicleVM.value.garagingLocation_ML.addressLine2);
    };
    
    const shouldValidateAddress = (vehicle) => {
        return isQnB && !vehicle.parkedAtPrimary_ML && !isPOBox() && isParkedInStateSameAsPrimary();
    };
    const validateAddress = useCallback(async (garageLocation) => {
        try {
            await MLAddressVerificationService.validateAddress(viewModelService, garageLocation, productCode);
        } catch (err) {
            return false;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const isFieldDefaultedByHVQ = (inputID) => {
        return submissionVM.value.isConsumerCompRater()
            && MLHVQDefaultFieldsUtil.isFieldDefaultedByHVQ(submissionVM.value.compRaterVendorName_ML, inputID, currentStep.mlStepName);
    };

    const nextVehicle = () => {
        if (isValid() && isNonOwnedSelected(page.currentVehicleVM.value)) {
            return;
        }

        if (page.currentIdx < sortedVehicleVMList.length - 1) {
            setPage(nextPageState(page.currentIdx + 1));
            window.scrollTo(0, 0);
        }
    };
    const nonOwnedGoToContactUs = () => {
        const isNonOwnedNormalFlowQnB = isQnB ? messages.mlUwIssueNormalFlowContentQB : messages.nonOwnedErrorMsg;
        const isNonOwnedNormalFlowContactUsQnB = isQnB ? messages.mlUwIssueNormalFlowQB : messages.nonOwnedContacUsMsg;
        const contactUsError = {
            customError: {
                appErrorMessages: [translator(isNonOwnedNormalFlowContactUsQnB)],
                contactUsMessage: isNonOwnedNormalFlowQnB,
                quoteID: _.get(submissionVM, 'quoteID.value'),
                isNonOwned: messages.nonOwned
            }
        };
        // eslint-disable-next-line max-len
        // Running Promise.reject will throw us out of wizard and to contact us, passing in error state
        Promise.reject((contactUsError));
    };

    const next = async () => {
        if (isValid() && isNonOwnedSelected(page.currentVehicleVM.value)) {
            nonOwnedGoToContactUs();
        }

        PATrackEvent('Quote Step Submitted', false, submissionVM.value, null, { step: 'VehiclesScreen'}, true);
        
        showProgressDialog(platformMessages.saveSubmission);
        // save and move to next page
        _.unset(submissionVM.value, 'bindData');
        _.set(submissionVM, 'portalCurrentStep_ML.value', currentStep.mlStepName);
        try {
            const res = await LoadSaveService.updateDraftSubmission(submissionVM.value);
             clearErrorAlerts('costNewValidation')
              if ( _.has(res, "proxySymbolsUsed_Ext") && res?.proxySymbolsUsed_Ext === true && !res?.costNew?.amount) {
                hideProgressDialog();
                addErrorAlert(messages.costNewRequiredQB, "costNewValidation");
                return false;
              }
            submissionVM.value = res;
            const nextStepName = currentStep.mlStepName === 'paHVQConsumerVehicleDetails' ? 'paHVQConsumerAddtlInfoToQuote' : 'paAddtlInfoToQuote';
            MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, nextStepName);
            if (!nextStepVisited()) {
                return submissionVM;
            }
            goNext();
        } finally {
            hideProgressDialog();
        }
        return false;
    };

    const onNext = async () => {
        if (isQnB) {
            const vehicles = submissionVM.value.lobData.personalAuto.coverables.vehicles;

            vehicles.forEach((vehicle) => {
                if (ViewModelUtil.isViewModelNode(vehicle.garagingLocation_ML)) {
                    vehicle.garagingLocation_ML = vehicle.garagingLocation_ML.value;
                }
            });
        }

        if (shouldValidateAddress(page.currentVehicleVM.value.garagingLocation_ML)) {
            await validateAddress(page.currentVehicleVM.garagingLocation_ML);
        }

        if (!isValid()) {
            return false;
        }

        setShowValidtionErrors(false);
        if (isFinalVehicle(page.currentVehicleVM)) {
            return next();
        }
        nextVehicle();
        refreshData();
        return false;
    };


    const editVehicle = (evt) => {
        if (isValid() && evt.index < page.currentIdx) {
            setPage(nextPageState(evt.index));
            window.scrollTo(0, 0);
        }
    };
    const VehicleSectionHeaderMsg = translator(messages.paVehicleMainSectionHeader, {
        index: page.currentIdx + 1,
        length: sortedVehicleVMList.length
    });


    const updateSubmissionVM = useCallback(() => {
        updateWizardData(submissionVM);
    }, [submissionVM, updateWizardData]);

    const setEditMode = useCallback((enabled) => {
        // eslint-disable-next-line no-return-assign
        paModel.vehicles.forEach((v) => v.editMode_ML = !!enabled);
    }, [paModel]);

    const setModifierEditMode = useCallback((editMode) => {
        paModel.vehicles.forEach((vehicle) => {
            if (vehicle.vehicleModifiers_ML) {
                vehicle.vehicleModifiers_ML.forEach((mod) => {
                    mod.editMode_ML = editMode;
                });
            }
        });
    }, [paModel]);

    const disableValidationForDependentModifiers = useCallback((mod) => {
        if (_.get(mod, 'hasDependentModifier_ML.value')) {
            paModel.vehicles.forEach((vehicle) => {
                if (!_.isEmpty(vehicle.vehicleModifiers_ML)) {
                    vehicle.vehicleModifiers_ML.forEach((modifier) => {
                        if (modifier.isDependentModifier_ML) {
                            modifier.editMode_ML = false;
                        }
                    });
                }
            });
        }
    }, [paModel]);

    const updateModifier = useCallback((mod) => {
        disableValidationForDependentModifiers(mod);
        // Disable validation so we can update the model and the vehicle modifiers with it
        setEditMode(false);
        const submissionLoadPromise = LoadSaveService.updateModifiers(submissionVM.value, authHeader);
        showProgressDialog(platformMessages.syncing, submissionLoadPromise, true);
        submissionLoadPromise.then((qdd) => {
            _.set(submissionVM, 'lobData.personalAuto.coverables.vehicles', qdd.lobData.personalAuto.coverables.vehicles);

        }, (error) => {
            // eslint-disable-next-line no-console
            console.error(error);
        }).finally(() => {
            setEditMode(true);
            setModifierEditMode(true);
            updateWizardData(submissionVM);
        });
    }, [authHeader, disableValidationForDependentModifiers, setEditMode, setModifierEditMode, showProgressDialog, submissionVM, updateWizardData]);

     const checkValidDate = (vehicle) => {
            if(!isModernProduct || vehicle?.category_ML !== 'private_passenger_np'){
                return true
            }
            const vPurchaseDate = vehicle.vehiclePurchaseDate;
            var isValidDate = true;
            if (!vPurchaseDate?.day || !vPurchaseDate?.month || !vPurchaseDate?.year) { return false; }
            const selected = new Date(vPurchaseDate?.year, vPurchaseDate?.month - 1, vPurchaseDate?.day);
            const today = new Date();
            const maxDate = new Date();
            maxDate.setDate(today.getDate() + 7);
            if (selected > maxDate) {
                return isValidDate = false;
            }
            const validDate = moment(
                { year: vPurchaseDate.year, month: vPurchaseDate.month - 1, day: vPurchaseDate.day },
                true).isValid();
            if (!validDate) return false;
            return isValidDate;
        }
    

    const nextStepText_Ext = () => {
        return isFinalVehicle(page.currentVehicleVM) ? (
            <FormattedMessage
                {...nextMessageFinalVehicle}
                values={
                    {
                        additionalinformation: (
                            <strong>
                                {
                                    translator(messages.additionalInformation)
                                }

                            </strong>
                        )
                    }
                }
            />
        ) : (
            <FormattedMessage
                {
                ...nextMessagePendingVehicles
                }
                values={
                    {
                        additionalvehicles: (
                            <strong>

                                {
                                    translator(messages.additionalVehicles)
                                }


                            </strong>
                        )
                    }
                }
            />
        );
    };

    const overrideProps = {
        '@field': {
            showOptional: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },

        vehiclesIterable: {
            data: sortedVehicleVMList
        },
        paEditVehicleComponent: {
            key: page.currentVehicleVM.publicID.value,
            visible: isPageInitialised,
            vehicleVM: page.currentVehicleVM,
            submissionVM: submissionVM,
            showValidtionErrors: showValidtionErrors,
            handleVehicleModelChange: handleVehicleModelChange,
            onValidate: onValidate,
            vehicleHeaderMessage: VehicleSectionHeaderMsg,
            bodyTypename: getBodyTypeName,
            showTrim: showTrim,
            showCylinders: showCylinders,
            policyAddress: page.policyAddress,
            effectiveDate: page.effectiveDate,
            updateViewModel: updateSubmissionVM,
            isFieldDefaultedByHvq: isFieldDefaultedByHVQ,
            isCompRaterDefaultsPage,
            purchaseDateInputError: checkValidDate,
            showGarageAddressError,
            resetGarageValidation: () => {
                setShowValidtionErrors(false);
            }
        },
        vehicleModifiers: {
            modifier: _.get(page.currentVehicleVM, 'vehicleModifiers_ML'),
            isHomeowners: false,
            modifierIdToAppend: _.get(page.currentVehicleVM, 'tempID.value'),
            updateModifier: updateModifier,
            onValidate: onValidate,
            showValidationErrors: showValidtionErrors,
            visible:shouldVisible()
        },
        editVehicleSection: {
            visible: isPageInitialised,
            className: isQnB ? 'col-md-8 qbPadding' : 'col-md-8'
        },
        nextDescription: {
            content: nextStepText_Ext()
        },
        nextButton: {
            className: isQnB ? 'primaryButton' : ''
        },
        ...generateVehicleListOverrides(),
        ...generateEditVehicleOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onNext: onNext,
            onValidate: onValidate,
            editVehicle: editVehicle
        },
        resolveClassNameMap: styles,
        resolveComponentMap: {
            mlpaeditvehiclecomponent: MLPAEditVehicleComponent,
            MLModifierComponent: MLModifierComponent
        }
    };

    const readValue =
        (id, path) => {
            return readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);
        };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            skipWhen={skipOnResumeIf}
            showNext={!nextStepVisited() && isFinalVehicle(page.currentVehicleVM)}
            onNext={onNext}
            nextLabel={translator(platformMessages.mlContinue)}
            showTextNextToButton={!nextStepVisited() && isFinalVehicle(page.currentVehicleVM)}
            textNextToButton={nextStepText_Ext()}
            metadataPageId={!nextStepVisited() && isFinalVehicle(page.currentVehicleVM) && _.get(metadata, 'pageContent.id')}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                onModelChange={updateWizardData}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
                showErrors={showValidtionErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
}

VehiclesPage.propTypes = wizardProps;
export default VehiclesPage;
